import { useEffect, useRef, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STARTER_PROMPTS = [
  "How much did I spend on dining this year, by month?",
  "Should I rebalance my portfolio?",
  "Which budgets am I closest to busting?",
  "What's my biggest savings opportunity?",
];

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div
      data-testid={`coach-msg-${msg.role}`}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
    >
      <div
        className={`max-w-[85%] rounded-md px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed
          ${isUser
            ? "bg-foreground text-background"
            : "bg-amber-50 border border-amber-200 text-slate-900"
          }`}
      >
        {msg.content}
      </div>
    </div>
  );
}

function AICoach({ currency }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const scrollRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/coach/messages`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    // Auto-scroll to bottom on new message
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const sendMessage = async (content) => {
    const text = (content ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    // Optimistic user message
    const tempUser = {
      message_id: `temp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUser]);
    try {
      const res = await fetch(`${API}/coach/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: text, currency }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => {
          // Replace optimistic temp with the real user msg + the assistant reply
          const filtered = prev.filter((m) => m.message_id !== tempUser.message_id);
          return [...filtered, data.user_message, data.assistant_message];
        });
      } else {
        toast.error("Coach is offline. Try again in a moment.");
        // Roll back optimistic user message
        setMessages((prev) => prev.filter((m) => m.message_id !== tempUser.message_id));
      }
    } catch {
      toast.error("Network error");
      setMessages((prev) => prev.filter((m) => m.message_id !== tempUser.message_id));
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear all messages with your AI Coach?")) return;
    setClearing(true);
    try {
      const res = await fetch(`${API}/coach/messages`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setMessages([]);
        toast.success("Conversation cleared");
      } else {
        toast.error("Clear failed");
      }
    } catch {
      toast.error("Clear failed");
    } finally {
      setClearing(false);
    }
  };

  return (
    <Card data-testid="ai-coach-card" className="border border-border mb-8">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold inline-flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
              AI Coach
            </CardTitle>
            <CardDescription>
              Ask anything about your finances. Answers are grounded in your real {currency} data.
            </CardDescription>
          </div>
          {messages.length > 0 && (
            <button
              data-testid="coach-clear-btn"
              onClick={handleClear}
              disabled={clearing}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors inline-flex items-center gap-1"
              title="Clear conversation"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              Clear
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div
          ref={scrollRef}
          data-testid="coach-messages-scroll"
          className="h-[360px] overflow-y-auto pr-1 border border-border rounded-md p-3 bg-slate-50/40"
        >
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading conversation…
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <Sparkles className="h-8 w-8 text-amber-500 mb-2" strokeWidth={1.5} />
              <p className="text-sm font-medium text-slate-900">Start a conversation</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">Pick a starter or ask your own</p>
              <div className="flex flex-col gap-2 w-full max-w-sm">
                {STARTER_PROMPTS.map((p, i) => (
                  <button
                    key={p}
                    data-testid={`coach-starter-${i}`}
                    onClick={() => sendMessage(p)}
                    disabled={sending}
                    className="text-left text-xs px-3 py-2 border border-border rounded-md hover:bg-amber-50 hover:border-amber-300 transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m) => (
                <MessageBubble key={m.message_id} msg={m} />
              ))}
              {sending && (
                <div data-testid="coach-typing" className="flex justify-start mb-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-md px-3.5 py-2.5 text-sm text-slate-600 inline-flex items-center">
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" strokeWidth={1.5} />
                    Thinking…
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="mt-3 flex gap-2"
        >
          <Input
            data-testid="coach-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Ask about your ${currency} finances…`}
            disabled={sending}
            maxLength={2000}
          />
          <Button
            data-testid="coach-send-btn"
            type="submit"
            disabled={sending || !input.trim()}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default AICoach;
